import { useEffect, useState, type ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDownload,
  faFileExcel,
  faFileCsv,
  faFileWord,
  faFileLines,
  type IconDefinition,
} from '@fortawesome/pro-regular-svg-icons';
import {
  Loader,
  SlideOutPanelShell,
  Stack,
  Text,
  slideOutPanelShellStyles as shellStyles,
  useExitRetained,
  useSlideOutPanelChrome,
} from '@cowbell/ui';
import { Button, ThemeIcon } from '@cowbell/ui/core';
import { triggerBrowserDownload } from '../../utils/download-delivery-result';
import { useAuthenticatedBlob } from '../../utils/authenticated-blob';
import {
  parseSpreadsheet,
  type ParsedSpreadsheet,
} from '../../utils/spreadsheet-parser';
import { parseDocx } from '../../utils/docx-parser';
import {
  extensionOf,
  resolveFilePreviewKind,
} from '../../utils/file-preview-kind';
import SpreadsheetPreviewTable from './SpreadsheetPreviewTable';
import type { Source } from '../../types';
import styles from './FilePreviewPanel.module.css';

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const EXCEL_EXTENSIONS = new Set(['.xlsx', '.xls', '.xlsm', '.xlsb']);
// Kept distinct from EXCEL_EXTENSIONS (bugbot PR feedback, OMNI-838): a
// plain delimited text table isn't an Excel workbook, and the File type
// row would otherwise contradict the CSV/TSV extension badge right next
// to it.
const DELIMITED_EXTENSIONS = new Set(['.csv', '.tsv']);
const WORD_EXTENSIONS = new Set(['.doc', '.docx']);

interface FileTypeIconMeta {
  readonly icon: IconDefinition;
  readonly color: string;
  readonly label: string;
  readonly caption: string;
}

const CANT_PREVIEW_INLINE =
  'can’t be previewed inline, so there’s nothing to render here.';

/** Picks the download card's icon/tint/copy by extension so the card reads
 *  as "here's your Excel file" rather than a generic, unstyled blank state
 *  (OMNI-838 UX follow-up) — same extension-keyed approach as the file-type
 *  icons in `AccountDocumentsModule`, kept local since this is the only
 *  consumer that needs a Mantine color plus this copy alongside the icon. */
function fileTypeIconMeta(fileName: string): FileTypeIconMeta {
  const ext = extensionOf(fileName);
  if (EXCEL_EXTENSIONS.has(ext)) {
    return {
      icon: faFileExcel,
      color: 'green',
      label: 'Excel Spreadsheet',
      caption: `Spreadsheets ${CANT_PREVIEW_INLINE}`,
    };
  }
  if (DELIMITED_EXTENSIONS.has(ext)) {
    return {
      icon: faFileCsv,
      color: 'green',
      label: ext === '.tsv' ? 'TSV File' : 'CSV File',
      caption: `Spreadsheets ${CANT_PREVIEW_INLINE}`,
    };
  }
  if (WORD_EXTENSIONS.has(ext)) {
    return {
      icon: faFileWord,
      color: 'blue',
      label: 'Word Document',
      caption: `This document format ${CANT_PREVIEW_INLINE}`,
    };
  }
  return {
    icon: faFileLines,
    color: 'gray',
    label: 'File',
    caption: `This file type ${CANT_PREVIEW_INLINE}`,
  };
}

interface FilePreviewPanelProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly source: Source | null;
  readonly chatWidth: number;
  readonly chatRight?: number;
}

/** Shared by the "still fetching the blob" and "blob fetched but no
 *  renderable content resolved yet" states — same markup, different
 *  trigger condition, so it's factored out rather than duplicated. */
function LoadingState() {
  return (
    <Stack align="center" justify="center" h="100%">
      <Loader size="md" />
      <Text size="sm" c="dimmed">
        Loading document…
      </Text>
    </Stack>
  );
}

/** Sibling of `PDFViewerPanel` for the uploaded-document types PDF doesn't
 *  cover: images render inline, CSV/XLSX parse into a table, DOCX renders as
 *  formatted HTML (OMNI-839), everything else (legacy .doc, unparseable
 *  spreadsheets, etc.) shows a download card. See OMNI-697 and the design
 *  doc's §3.4 for why this is a new component rather than an extension of
 *  `PDFViewerPanel`.
 *
 *  Chrome (backdrop, drag-resize, expand/collapse, focus trap, Esc) comes
 *  from `useSlideOutPanelChrome` + `SlideOutPanelShell`, the same pair
 *  `CvePanel` and `ThreatActorPanel` use — this panel previously hardcoded a
 *  640px width with its own header markup and none of that behaviour. */
export default function FilePreviewPanel({
  isOpen,
  onClose,
  source,
  chatWidth,
  chatRight = 0,
}: FilePreviewPanelProps) {
  // On close the caller clears `source` in the same turn; the panel keeps what it showed while it slides away.
  const { value: shown, exiting } = useExitRetained(source, isOpen && !!source);

  const rawFileUrl = shown?.url ?? '';
  const fileName = shown?.title ?? 'Document';
  const kind = shown ? resolveFilePreviewKind(shown) : 'download-only';

  const chrome = useSlideOutPanelChrome({
    isOpen,
    hasSource: !!source,
    onClose,
    chatWidth,
    chatRight,
  });
  const { visible } = chrome;

  const {
    blob,
    isLoading: isBlobLoading,
    error: blobError,
  } = useAuthenticatedBlob(rawFileUrl, visible || exiting);

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string | null>(null);
  const [parsed, setParsed] = useState<ParsedSpreadsheet | null>(null);
  const [docxHtml, setDocxHtml] = useState<string | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);

  useEffect(() => {
    setImageUrl(null);
    setTextContent(null);
    setParsed(null);
    setDocxHtml(null);
    setContentError(null);

    if (!blob) return undefined;

    if (kind === 'image') {
      const url = URL.createObjectURL(blob);
      setImageUrl(url);
      return () => URL.revokeObjectURL(url);
    }

    let cancelled = false;
    if (kind === 'text') {
      blob
        .text()
        .then((text) => {
          if (!cancelled) setTextContent(text);
        })
        .catch(() => {
          if (!cancelled) setContentError('Failed to read document content.');
        });
    } else if (kind === 'csv' || kind === 'tsv' || kind === 'xlsx') {
      parseSpreadsheet(blob, kind)
        .then((result) => {
          if (!cancelled) setParsed(result);
        })
        .catch(() => {
          if (!cancelled)
            setContentError('Failed to parse spreadsheet content.');
        });
    } else if (kind === 'docx') {
      parseDocx(blob)
        .then((result) => {
          if (!cancelled) setDocxHtml(result.html);
        })
        .catch(() => {
          if (!cancelled) setContentError('Failed to parse document content.');
        });
    }
    return () => {
      cancelled = true;
    };
  }, [blob, kind]);

  const handleDownload = () => {
    if (blob) triggerBrowserDownload(blob, fileName);
  };

  const isSpreadsheetKind = kind === 'csv' || kind === 'tsv' || kind === 'xlsx';
  const parsedEmpty =
    isSpreadsheetKind && !!parsed && parsed.columns.length === 0;
  const showDownloadCard =
    kind === 'download-only' || !!contentError || parsedEmpty;

  // Early-return dispatch rather than a chained ternary (PR feedback on
  // OMNI-697) — same six render states as before, easier to scan.
  function renderContent(): ReactNode {
    if (isBlobLoading) return <LoadingState />;

    if (blobError) {
      return (
        <Stack align="center" justify="center" h="100%">
          <Text size="sm" c="red">
            {blobError}
          </Text>
        </Stack>
      );
    }

    if (showDownloadCard && blob) {
      const { icon, color, label, caption } = fileTypeIconMeta(fileName);
      const extLabel = extensionOf(fileName).replace('.', '').toUpperCase();
      // A white info card floating on a softly tinted backdrop (OMNI-838 UX
      // follow-up, per design reference) — the tint fills the whole pane so
      // the state doesn't read as empty, while the card itself (not a flat
      // wash alone) keeps it reading as a deliberate panel, not a loading or
      // error state. Metadata rows are limited to fields the citation
      // actually carries (file type, size) — Source has no uploader/origin
      // metadata for spreadsheet/document/image citations, so those rows
      // aren't fabricated the way a mocked-up reference design might show.
      return (
        <div className={styles.downloadBackdrop}>
          <div className={styles.downloadCard}>
            <div className={styles.downloadCardHeader}>
              <ThemeIcon size={56} radius="md" color={color} variant="light">
                <FontAwesomeIcon icon={icon} size="lg" />
              </ThemeIcon>
              <div className={styles.downloadCardHeaderText}>
                <Text size="sm" fw={600} className={styles.downloadFileName}>
                  {fileName}
                </Text>
                <div className={styles.downloadMetaRow}>
                  {extLabel && (
                    <span
                      className={styles.extBadge}
                      style={{
                        background: `var(--mantine-color-${color}-light)`,
                        color: `var(--mantine-color-${color}-light-color)`,
                      }}
                    >
                      {extLabel}
                    </span>
                  )}
                  <Text size="xs" c="dimmed">
                    {formatFileSize(blob.size)}
                  </Text>
                </div>
              </div>
            </div>
            <div className={styles.downloadDivider} />
            <div className={styles.downloadMetaList}>
              <div className={styles.downloadMetaListRow}>
                <Text size="xs" c="dimmed">
                  File type
                </Text>
                <Text size="xs" fw={500}>
                  {label}
                </Text>
              </div>
              <div className={styles.downloadMetaListRow}>
                <Text size="xs" c="dimmed">
                  Size
                </Text>
                <Text size="xs" fw={500}>
                  {formatFileSize(blob.size)}
                </Text>
              </div>
            </div>
            <Button
              fullWidth
              leftSection={<FontAwesomeIcon icon={faDownload} />}
              onClick={handleDownload}
            >
              Download
            </Button>
          </div>
          <Text size="xs" c="dimmed" className={styles.downloadCaption}>
            {caption}
          </Text>
        </div>
      );
    }

    if (kind === 'image' && imageUrl) {
      return (
        <div className={styles.imageWrap}>
          <img src={imageUrl} alt={fileName} className={styles.image} />
        </div>
      );
    }

    if (kind === 'text' && textContent !== null) {
      return <pre className={styles.textPreview}>{textContent}</pre>;
    }

    // docxHtml is DOMPurify-sanitized in parseDocx before it ever reaches
    // state — see the sibling sanitizeEmailHtml path for the same pattern.
    if (kind === 'docx' && docxHtml !== null) {
      return (
        <div
          className={styles.docxPreview}
          dangerouslySetInnerHTML={{ __html: docxHtml }}
        />
      );
    }

    if (isSpreadsheetKind && parsed) {
      // Truncation/extra-sheet notes tell the user to "download for full
      // data" — the table view itself needs its own download control to
      // act on that, since it's a separate branch from the download-only
      // card above (PR feedback on OMNI-697).
      const needsDownloadAffordance =
        parsed.truncated || parsed.extraSheetCount > 0;
      return (
        <div className={styles.tableWrap}>
          {parsed.truncated && (
            <Text size="xs" c="dimmed" className={styles.truncationNote}>
              Showing first {parsed.rows.length} of {parsed.totalRows} rows —
              download for full data.
            </Text>
          )}
          {parsed.extraSheetCount > 0 && (
            <Text size="xs" c="dimmed" className={styles.truncationNote}>
              {parsed.extraSheetCount} more sheet
              {parsed.extraSheetCount === 1 ? '' : 's'} in this file — download
              to view.
            </Text>
          )}
          {needsDownloadAffordance && blob && (
            <Button
              size="xs"
              variant="subtle"
              className={styles.downloadBtn}
              leftSection={<FontAwesomeIcon icon={faDownload} />}
              onClick={handleDownload}
            >
              Download full file
            </Button>
          )}
          <SpreadsheetPreviewTable
            rows={parsed.rows}
            columns={parsed.columns}
          />
        </div>
      );
    }

    // The source carried no usable url, so no request was ever made and a
    // spinner here could never resolve (OMNI-742). Keyed on the url itself
    // rather than on `!blob`, which is also true for the frame between mount
    // and the fetch effect running, and for a blob still being parsed — both
    // are genuine loading states that fall through to LoadingState below.
    if (!rawFileUrl) {
      return (
        <Stack align="center" justify="center" h="100%">
          <Text size="sm" c="dimmed">
            Preview isn’t available for this document.
          </Text>
        </Stack>
      );
    }

    return <LoadingState />;
  }

  return (
    <SlideOutPanelShell
      chrome={chrome}
      onClose={onClose}
      styles={shellStyles}
      headerTitle={fileName}
      headerActionsStart={
        blob && (
          <button
            className={shellStyles.headerBtn}
            onClick={handleDownload}
            title="Download"
            aria-label="Download file"
            type="button"
          >
            <FontAwesomeIcon icon={faDownload} />
          </button>
        )
      }
    >
      {renderContent()}
    </SlideOutPanelShell>
  );
}
